// Shared type definitions for Helios CapitalMarketsOS

export type FocusAssetId = 'enterprise' | 'cre' | 'cmbs' | 'rmbs' | 'clo' | 'whole-loans'
export type PersonaId =
  | 'executive'
  | 'originator'
  | 'asset-manager'
  | 'portfolio-manager'
  | 'secondary-trader'
  | 'risk-manager'
  | 'structurer'

export type PlatformModuleId =
  | 'dashboard-legal'
  | 'dashboard-underwriting'
  | 'dashboard-asset'
  | 'dashboard-ceo'
  | 'command-center'
  | 'db-search'
  | 'pipeline'
  | 'intake'
  | 'screening'
  | 'underwriting'
  | 'committee'
  | 'closing'
  | 'surveillance'
  | 'asset-management'
  | 'valuation'
  | 'market-intelligence'
  | 'market-news'
  | 'reports'
  | 'crm'
  | 'loan-comparison'
  | 'portfolio-analytics'
  | 'term-sheet'
  | 'hedge-desk'
  | 'document-vault'
  | 'investor-portal'
  | 'borrower-portal'
  | 'bi-dashboard'
  | 'comp-database'
  | 'workflow-automation'
  | 'golden-deal-reconciliation'
  | 'credit-box'

export interface GoldenReconRow {
  model: string
  display: string
  status: 'RUN' | 'PENDING'
  fidelity?: Record<string, boolean>
  fidelity_all_exact?: boolean
  screen_outcome?: string
  exceptions?: string[]
  referrals?: string[]
  rules_version?: string
  proceeds_shortfall?: {
    ask: number
    supportable: number
    gap: number
    supportable_pct_of_ask: number
  }
  reconciliation: {
    verdict: string
    actual: string
    actual_reasons: string[]
    note: string
  }
}

export interface GoldenReconMatrix {
  artifact: string
  rules_version: string
  rules_sha256?: string
  coverage: string
  corresponds: string
  all_exact_fidelity: boolean
  rows: GoldenReconRow[]
}

// ── Golden-deal run trace (stage-by-stage demonstration) ─────────────────

export interface TraceAnchor {
  metric: string
  sheet: string
  label_cell: string
  expect_label: string
  value_cell: string
  value: number | string | null
}

export interface TraceComputedStep {
  metric: string
  formula: string
  inputs: Record<string, number | string>
  engine_value: number | string | null
  workbook_value: number | string
  workbook_key: string
  exact: boolean
  // Raw engine-minus-workbook difference, emitted only when exact is false —
  // display values are rounded to 9dp, so without this a real divergence could
  // render as two identical numbers labeled Mismatch.
  delta?: number
}

export interface TraceCheck {
  name: string
  outcome: 'PASS' | 'EXCEPTION' | 'REFER' | 'MISSING_INPUT'
  detail: string
  missing_field: string | null
  fill_hint: string | null
}

export interface TraceScreenInput {
  field: string
  value: number | string
  source_key: string
  source_cell: string | null
}

export interface TraceAttachment {
  name: string
  cumulative_amount: number
  cumulative_ltv: number | null
  cumulative_debt_yield: number | null
}

export interface GoldenDealTraceResponse {
  model: string
  display: string
  rules_version: string
  stages: {
    source: { workbook: string; anchors: TraceAnchor[]; doctrine: string }
    record: { values: Record<string, number | string>; note: string }
    computed: { steps: TraceComputedStep[]; all_exact: boolean; tolerance: string; note: string }
    screen: {
      inputs: TraceScreenInput[]
      program: string
      program_name: string
      rules_version: string
      rules_sha256: string
      checks: TraceCheck[]
      attachments: TraceAttachment[]
    }
    verdict: {
      outcome: string
      exceptions: string[]
      referrals: string[]
      missing_inputs: { check: string; field: string | null; fill_hint: string | null }[]
      reconciliation: GoldenReconRow['reconciliation']
      proceeds_shortfall?: GoldenReconRow['proceeds_shortfall']
    }
  }
}

export interface WhatIfMissingResponse {
  model: string
  display: string
  withheld_field: string
  withheld_source_cell: string | null
  sandbox: boolean
  screen: GoldenDealTraceResponse['stages']['screen']
  verdict: {
    outcome: string
    missing_inputs: { check: string; field: string | null; fill_hint: string | null }[]
  }
  note: string
}

export interface CreditBoxResponse {
  rules: {
    version: string
    source: string
    status: string
    decisions: Record<string, string>
    tranche_seniority: string[]
    programs: Record<string, {
      name: string
      max_ltv: number
      ltv_basis: string
      min_debt_yield_stabilized: number | null
      min_dscr_amortizing: number | null
      loan_size_min: number
      loan_size_max: number
      asset_classes: string | string[]
      construction: boolean
      geographies: string[]
    }>
    conventions: Record<string, number | string | string[]>
    judgment_factors: string[]
  }
  artifact_sha256: string
  note: string
}

export type LoanPhase = 'Production' | 'Trading' | 'Asset Management'

/**
 * Confirmed origination stage model. Triage is automatic on receipt and
 * pre-assignment; Pre-screen Indication is verbal with nothing written;
 * Post-screen Quote is a two-page quote letter; Term Sheet Signed means the
 * deposit is in and underwriting is running.
 *
 * There is deliberately no stage between Term Sheet Signed and IC / Committee
 * Approved. Rate Lock is optional and used sparingly — floating-rate deals
 * never lock. Server-side source of truth: GET /loans/stages.
 */
export type LoanStage =
  | 'Triage'
  | 'Pre-screen Indication'
  | 'Post-screen Quote'
  | 'Term Sheet Issued'
  | 'Term Sheet Signed'
  | 'IC / Committee Approved'
  | 'Rate Lock'
  | 'Funded'

/** Canonical ordering; the index into this array is the stage_index. */
export const LOAN_STAGES: readonly LoanStage[] = [
  'Triage',
  'Pre-screen Indication',
  'Post-screen Quote',
  'Term Sheet Issued',
  'Term Sheet Signed',
  'IC / Committee Approved',
  'Rate Lock',
  'Funded',
] as const

/** Stages a deal may skip without its progression being incomplete. */
export const OPTIONAL_LOAN_STAGES: readonly LoanStage[] = ['Rate Lock'] as const

export type AssetClass = 'Office' | 'Industrial' | 'Multifamily' | 'Retail' | 'Hospitality' | 'Lab/R&D' | 'Mixed-Use' | 'Land' | 'Senior Living' | 'Construction' | 'Self-Storage' | 'Student Housing'

export type ProductType = 'Senior Mortgage' | 'Large Loan' | 'B-Note' | 'Mezzanine' | 'Ground Lease'

export type RateIndex = 'SOFR' | 'UST' | 'Fixed'

export type TriggerStatus = 'overdue' | 'pending' | 'upcoming' | 'resolved'
export type TriggerSeverity = 'high' | 'medium' | 'low'

export interface Trigger {
  id: string
  type: string
  due: string
  status: TriggerStatus
  severity: TriggerSeverity
  description?: string
}

export interface CashFlowData {
  historicNOI: number
  prospectiveNOI: number
  variability: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High'
  expenseRatio: number
  revenue: number
  expenses: number
  rents: number
  leaseVelocity: 'Very Strong' | 'Strong' | 'Moderate' | 'Weak'
  extraordinaryCapital?: number
  /** Stated NCF from intake. Never the same slot as prospectiveNOI. */
  ncf?: number | null
  occupancy?: number | null
  annualDebtService?: number | null
}

export interface MarketData {
  msa: string
  vacRate: number
  rentGrowth: number
  capRate: number
  absorption: number
}

export interface ModelComparison {
  source: 'Bloomberg' | "Moody's" | 'S&P' | 'Proprietary'
  dscr: number
  ltv: number
  projectedNOI: number
  defaultProbability?: number
  spreadToTreasury?: number
}

export interface CapitalStackLayer {
  type: ProductType
  amount: number
  rate: string
  spread: number
  index: RateIndex
  ltv: number
  maturity: string
  term: string
}

export interface DealSizing {
  propertyValue: number
  noi: number
  capRate: number
  maxLtv: number
  maxLoanAmount: number
  minDscr: number
  debtYield: number
  suggestedStructure: CapitalStackLayer[]
}

export interface CreditMetrics {
  ltv: number
  dscr: number
  debtYield: number
  irr?: number
  capRate: number
  noiMargin: number
}

export interface RentRollEntry {
  tenant: string
  suite: string
  sf: number
  monthlyRent: number
  annualRent: number
  rentPerSf: number
  leaseStart: string
  leaseEnd: string
  escalation: string
  status: 'Current' | 'MTM' | 'Vacant'
}

export interface StressScenario {
  name: string
  vacancyShock: number
  rentDecline: number
  capRateExpansion: number
  resultingDscr: number
  resultingLtv: number
  resultingDebtYield: number
  pass: boolean
}

export interface CommitteeMemo {
  dealId: string
  status: 'Draft' | 'In Review' | 'Circulated' | 'Approved' | 'Declined'
  preparedBy: string
  committeeDate: string
  riskRating: 'Low' | 'Moderate' | 'Elevated' | 'High'
  conditions: string[]
  approvers: { name: string; role: string; voted: boolean; vote?: 'Approve' | 'Decline' | 'Conditional' }[]
  sections: {
    executiveSummary: string
    propertyDescription: string
    marketAnalysis: string
    cashFlowSummary: string
    structure: string
    riskFactors: string
    recommendation: string
  }
}

export interface ClosingChecklistItem {
  id: string
  label: string
  status: 'Complete' | 'Pending' | 'Waived' | 'In Progress'
  responsible: string
  dueDate: string
  notes?: string
}

export interface ClosingChecklist {
  category: string
  items: ClosingChecklistItem[]
}

export interface ValuationRecord {
  propertyId: string
  propertyName: string
  valuationDate: string
  appraiser: string
  asIsValue: number
  stabilizedValue: number
  capRate: number
  discountRate: number
  terminalCapRate: number
  noi: number
  occupancy: number
  /** 'No Appraisal': a live loan with nothing ordered or drafted yet. */
  status: 'Awaiting Draft' | 'In Review' | 'Finalized' | 'No Appraisal'
  priorValue?: number
  valueChange?: number
  valueChangePct?: number
  valuationType?: 'pre-deal' | 'post-close'
  dealId?: string
  purpose?: string
  /** How the as-is value was arrived at when no document states it:
   *  "Derived: <formula>". Absent for a recorded value. */
  valueBasis?: string
}

export interface DevelopmentFeasibility {
  projectName: string
  totalCost: number
  gdv: number
  margin: number
  irr: number
  npv: number
  profitOnCost: number
  equityRequired: number
  debtRequired: number
  constructionPeriod: number
  scenarios: { name: string; margin: number; irr: number; npv: number }[]
}

/** A later email's figure waiting for a person before it may replace the
 *  deal's value (rule R5). Read back from the intake record onto every loan
 *  payload, so no screen shows the older figure as if nothing newer existed. */
export interface LoanPendingRevision {
  ingestionId: string
  field: string
  loanField?: string
  current: unknown
  incoming: unknown
  source?: string
  subject?: string
  from?: string
  receivedAt?: string | null
  why: string
  /** The lender's own draft terms (written by the firm's staff): a proposal
   *  a person may adopt, never the broker's revision of the deal. */
  lenderDraft?: boolean
}

/** An open question the platform knows about on this deal: a Data Intelligence Review
 *  conflict on its record, or an item the reconciliation sweep left for a
 *  person (stress tests OVR-09, CRE-26). */
export interface LoanDataConflict {
  field: string | null
  kind: string
  detail?: string | null
  source: string
  ingestionId?: string | null
  asOf?: string | null
}

export interface Loan {
  id: string
  property: string
  type: AssetClass
  location: string
  borrower: string
  sponsor: string
  sponsorCredit: number
  loanAmount: number
  /** The unit the loan's amounts are recorded in. Absent means DOLLARS — what
   *  the loans API returns. Only a book that is kept in millions (the curated
   *  demo deals) declares 'USD_MM'; a unit is never guessed from a figure's
   *  size (package M's one amount rule, stress test CMP-08). */
  amountUnit?: 'USD' | 'USD_MM'
  fundedAmount?: number | null
  fundedAt?: string | null
  brokerName?: string | null
  brokerId?: string | null
  /** The CRM's verdict on the deal's broker, attached on the live book from
   *  `GET /brokers/` (hooks/useBrokerStatus.ts). Absent until it answers, or
   *  for a reader whose role may not read the CRM (UI-05). */
  brokerBlocklisted?: boolean
  brokerPreferred?: boolean
  sourceIngestionId?: string | null
  pendingRevisions?: LoanPendingRevision[]
  dataConflicts?: LoanDataConflict[]
  ltv: number
  dscr: number
  debtYield?: number
  stage: LoanStage
  stageIndex: number
  phase: LoanPhase
  assignee: string
  daysOpen: number
  /** Days in the CURRENT stage — the register's E5 aging metric. Never
   *  cumulative. Undefined when the deal has no usable start timestamp, which
   *  renders as "—" rather than a misleading 0. */
  daysInStatus?: number | null
  /** New York calendar day the deal entered its current stage (same instant
   *  daysInStatus bills). Absent when the start cannot be dated. */
  currentStatusDate?: string | null
  /** The stage the newest transition left. Absent when the deal has never moved. */
  previousLoanStatus?: string | null
  /** Cumulative age of the deal. Kept distinct from daysInStatus on purpose —
   *  conflating them is the defect E5 reports. For a deal brought in from another
   *  system it counts from that system's last status date (`totalDaysOpenBasis`),
   *  not from the upload; for any other deal, from when it was opened. */
  totalDaysOpen?: number | null
  /** Where `totalDaysOpen` counts from: the source's last status date, the entry
   *  date, or the entry date as a fallback for an imported deal whose status date
   *  is missing or unreadable. */
  totalDaysOpenBasis?: 'source_status_date' | 'entry_date' | 'entry_date_fallback' | null
  /** The assignment clock (register F1/F2), returned on every loan read by
   *  `_with_aging`. A SECOND clock, deliberately distinct from daysInStatus:
   *  that one measures how long a deal has sat in a stage, this one measures
   *  how long it has sat with a person since being assigned to them, counted in
   *  business hours against the workspace's SLA policy. A deal can be young in
   *  its stage and long overdue with its owner. */
  sla?: {
    /** False when nothing is assigned, so no clock is running to breach. */
    running: boolean
    owner: string | null
    /** Null when the clock is not running or has no usable start. */
    elapsedBusinessDays: number | null
    thresholdDays: number
    breached: boolean
    /** Past the further escalation window beyond the breach. */
    escalated: boolean
    dueAt: string | null
  } | null
  /** The quote clock and the closing clock (Chris Young, 26 Oct 2026; punch list
   *  CY-08/09/10), computed by `app.underwriting.deal_clocks` on every loan read.
   *  The quote ages only at Post-screen Quote, from the quote letter date (or,
   *  said as such, from the stage move); the close-by date is the deposit wire
   *  plus the closing window. Unknown is null, never 0. */
  dealClocks?: {
    quoteLetterDate: string | null
    quoteAgeDays: number | null
    quoteAgedFrom: 'quote_letter' | 'stage_move' | null
    quoteStale: boolean
    staleQuoteDays: number
    depositWireDate: string | null
    closeByDate: string | null
    daysToCloseBy: number | null
    closingWindowDays: number
  } | null
  maturity?: string
  rate: string
  /** Date the deal was received (YYYY-MM-DD) — the catalogue's "Date
   *  Received". On an intake deal it is the source email's date; the desk
   *  can edit it. Not a funding date, and not when the record was created
   *  (that is `createdAt`). */
  originated: string
  /** When the deal record entered the pipeline: the moment intake parsed the
   *  inquiry and promoted it. Server-stamped on insert, so always present. */
  createdAt?: string | null
  /** Version token for If-Match on PUT /loans/{id} (CMP-04). */
  updatedAt?: string | null
  priority: 'High' | 'Medium' | 'Low'
  /** True while the priority is the platform's default — no rule or person
   *  has set it. Shown as a Suggestion, never as the desk's call (MOD-32). */
  prioritySuggested?: boolean
  /** Sponsor / Borrower is the borrowing entity copied over because no
   *  document names a sponsor: a Suggestion, not a stated sponsor (Round 3). */
  sponsorSuggested?: boolean
  /** Fields whose value is only the platform's entry default (e.g. `rate_type`,
   *  `capital_program`): GET /loans `platform_defaults`. Prints mark them "(default)". */
  platformDefaults?: string[]
  cashFlows: CashFlowData
  market: MarketData
  triggers: Trigger[]
  documents: string[]
  models?: ModelComparison[]
  product?: ProductType
  capitalStack?: CapitalStackLayer[]
  rentRoll?: RentRollEntry[]
  stressTests?: StressScenario[]
  committeeMemo?: CommitteeMemo
  closingChecklist?: ClosingChecklist[]
  propertyValue?: number
  noi?: number
  sf?: number
  units?: number
  /** Native count of units/keys/beds — never a square-foot area. */
  unitCount?: number | null
  /** Native building area in square feet. Independent of `unitCount`. */
  squareFeet?: number | null
  yearBuilt?: number
  customFields?: Record<string, unknown>
  /** Catalogue fields whose current value the platform derived rather than a
   *  document stated (latest lineage event `inferred`). A derived ratio is
   *  flagged for confirmation, never raised as a credit exception. */
  derivedFields?: string[]
  /** The formula of each derived value a CURRENT computation made (a live
   *  rule, or a component valuation), keyed by field. A value a retired
   *  back-solve left is in `derivedFields` but has no formula here. Keys
   *  arrive camel-cased (`propertyValue`) through toCamelCase. */
  derivedFormulas?: Record<string, string>
  /** The platform's one completeness measure, computed by the API on every
   *  loan read: catalogue fields holding a value, out of the catalogue. */
  fieldCompleteness?: {
    populated: number
    total: number
    pct: number
    /** "30 of 127 catalogue fields" — what the percentage counts. */
    basis: string
  } | null
  notes?: Array<{ id: string; body: string; author: string; createdAt: string }>
  /** Written sections stored on the loan row: executive summary, market
   *  narrative, sponsor narrative and so on. Read by the dossier's Journal and
   *  Overview highlights; the API has always returned it. */
  narratives?: Record<string, unknown> | null
  /** Take-out / capital source — the "Lender" party on the dossier. */
  investor?: string | null
  /** Internal producer of the deal. */
  originator?: string | null
  /** The lending program the deal sits in. */
  capitalProgram?: string | null
  /** Credit-box checks outside the deal's program envelope, read from the
   *  screen recorded at intake (`GET /dashboards/exceptions`) — the one
   *  exception definition every surface shares. `null` = never screened;
   *  absent = the screen could not be loaded for this reader. */
  creditBoxExceptions?: string[] | null
  /** What each credit-box ratio check was computed on, flagging a stand-in
   *  (loan / purchase price, in-place NOI) — exec-03. */
  creditBoxBasis?: Array<{
    check: string
    input: string
    selectedFrom: string
    label: string
    value: number | null
    standIn: boolean
    text: string
  }> | null
  /** 'Credit Box v2.0 — awaiting client sign-off' while the ruleset is unratified. */
  creditBoxRulesNote?: string | null
  /** Master-record links. Written by nothing on the intake path today, so the
   *  dossier context also resolves the asset and sponsor by name. */
  propertyId?: string | null
  sponsorId?: string | null
  dealStatus?: string | null
  deadReason?: string | null
  fundingNotes?: string | null
  /** The kill timestamp as the loan LIST serves it (the column's own name,
   *  `dead_at`). `loansService` mirrors it onto `killedAt`, which is the name
   *  every screen and the kill/reopen responses use — read `killedAt`. */
  deadAt?: string | null
  /** Kill record. Present only on dead deals — the deal is retained, not deleted. */
  killedAt?: string | null
  killedAtStage?: string | null
  killReasonCode?: string | null
  killReasonNote?: string | null
  killedBy?: string | null
  /** F4/F5 broker-contact attestation captured at kill time. */
  killBrokerContacted?: boolean | null
  killContactMethod?: string | null
  killContactDate?: string | null
}

/** One entry in the kill-reason picklist, served by the API. */
export interface KillReason {
  code: string
  label: string
}

export interface LoanKillRecord {
  killed: boolean
  killedAt: string | null
  killedAtStage: string | null
  killReasonCode: string | null
  killReasonNote: string | null
  killedBy: string | null
  /** F4/F5 broker-contact attestation. */
  brokerContacted?: boolean | null
  contactMethod?: string | null
  contactDate?: string | null
}

/** Contact method for the F4/F5 broker attestation. */
export type KillContactMethod = 'call' | 'email'

/** The kill request payload the client sends to POST /loans/{id}/kill. */
export interface LoanKillInput {
  reasonCode: string
  note?: string
  /** Required yes/no: was the broker told the deal is dead? */
  brokerContacted: boolean
  /** Required when brokerContacted is true. */
  contactMethod?: KillContactMethod
  /** ISO date (YYYY-MM-DD), required when brokerContacted is true. */
  contactDate?: string
}

export interface ConversionStage {
  stage: string
  count: number
  rate: number
  color: string
}

export interface PipelineMetrics {
  totalVolume: number
  dealCount: number
  /** Mean over deals with a figure on record; null when none has one. */
  avgLTV: number | null
  avgDSCR: number | null
  inClosing: number
  overdueTriggers: number
  ytdOriginations?: number
  avgDaysToClose?: number
  totalAum?: number
  wtdAvgDebtYield?: number
}
